"use client"

import Image from "next/image"
import { motion } from "motion/react"

import { Marquee } from "@/components/ui/marquee"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"

const brands = [
  { name: "AKE", logo: "/images/brands/ake.jpg" },
  { name: "MFLS Forézienne", logo: "/images/brands/mfls-forezienne.jpg" },
  { name: "Jaguar", logo: "/images/brands/jaguar.jpg" },
  { name: "CMT", logo: "/images/brands/cmt.jpg" },
  { name: "Leman", logo: "/images/brands/leman.jpg" },
  { name: "Makita", logo: "/images/brands/makita.png" },
  { name: "Lamello", logo: "/images/brands/lamello.svg" },
  { name: "Virutex", logo: "/images/brands/virutex.svg" },
  { name: "Alsafix", logo: "/images/brands/alsafix.jpg" },
  { name: "Senco", logo: "/images/brands/senco.jpg" },
  { name: "KMR", logo: "/images/brands/kmr.jpg" },
  { name: "Ceratizit", logo: "/images/brands/ceratizit.png" },
  { name: "Piher", logo: "/images/brands/piher.png" },
  { name: "SIA Abrasifs", logo: "/images/brands/sia.svg" },
  { name: "ABAC", logo: "/images/brands/abac.jpg" },
  { name: "Promac", logo: "/images/brands/promac.png" },
  { name: "Alpha Coupe", logo: "/images/brands/alpha-coupe.jpg" },
  { name: "Ekamant", logo: "/images/brands/ekamant.jpg" },
  { name: "SDC Julia", logo: "/images/brands/julia.jpg" },
]

const ease = [0.25, 0.4, 0, 1] as const

export function BrandMarquee() {
  return (
    <section className="section-tight px-4 md:px-6">
      <div className="mx-auto max-w-7xl">
        {/* Section heading */}
        <motion.div
          className="mb-12 text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          transition={{ staggerChildren: 0.1 }}
        >
          <motion.div
            variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0 } }}
            transition={{ duration: 0.5, ease }}
          >
            <Badge variant="secondary" className="mb-4">Nos partenaires</Badge>
          </motion.div>
          <motion.h2
            className="text-2xl font-bold tracking-tight text-foreground md:text-3xl"
            style={{ textWrap: "balance" }}
            variants={{ hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } }}
            transition={{ duration: 0.6, ease }}
          >
            Ils nous font confiance
          </motion.h2>
        </motion.div>

        {/* Festool Premium — separate highlighted row */}
        <motion.div
          className="mb-8 flex flex-col items-center justify-center gap-4 sm:flex-row"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease }}
        >
          <motion.div
            className={cn(
              "flex items-center gap-4 rounded-2xl border-2 border-primary/30 bg-primary/5 px-6 py-4",
              "transition-colors hover:border-primary/50 hover:bg-primary/10"
            )}
            whileHover={{ y: -2, transition: { duration: 0.2 } }}
          >
            {/* Logo alt is descriptive (not just "Festool" which
             * squirrel flagged as redundant-matches-filename). The
             * surrounding text already says "FESTOOL" + "Partenaire
             * Premium"; saying "Logo Festool — partenaire premium
             * ADIS 95" gives AT users context beyond the filename. */}
            <Image
              src="/images/brands/festool.svg"
              alt="Logo Festool — partenaire premium ADIS 95"
              width={48}
              height={20}
              loading="lazy"
              className="h-6 max-w-[48px] rounded-lg object-contain"
            />
            <div>
              <p className="font-bold text-foreground">FESTOOL</p>
              <div className="flex gap-2 mt-1">
                <Badge className="bg-primary text-primary-foreground text-[10px]">Partenaire Premium</Badge>
                <Badge variant="outline" className="border-primary text-primary text-[10px]">Test Center</Badge>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Brand logo marquee */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2, ease }}
        >
          <Marquee className="[--duration:45s]">
            {brands.map((brand) => (
              <div
                key={brand.name}
                className={cn(
                  "flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3",
                  "transition-[transform,opacity,box-shadow,ring-color,border-color,background-color,color] duration-200 ease-[var(--ease-out-expo)]",
                  "hover:border-primary/30 hover:shadow-sm hover:-translate-y-0.5"
                )}
              >
                <Image
                  src={brand.logo}
                  alt={`Logo ${brand.name} — marque distribuée par ADIS 95`}
                  width={40}
                  height={40}
                  loading="lazy"
                  // 19 brand logos in this marquee fire as soon as
                  // hydration starts. Without `loading="lazy"` they
                  // race the home hero LCP image for connection slots
                  // and add 100–200 ms to LCP per the perf audit.
                  className="h-8 w-8 rounded object-contain"
                />
                <span className="text-sm font-medium text-foreground whitespace-nowrap">
                  {brand.name}
                </span>
              </div>
            ))}
          </Marquee>
        </motion.div>
      </div>
    </section>
  )
}
